// One page crashing must never blank the whole admin. Resets when the route changes.
import { Component, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { Bug, RotateCw } from 'lucide-react'

class Boundary extends Component<{ children: ReactNode; resetKey: string }, { error: Error | null }> {
  override state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  override componentDidCatch(error: Error, info: { componentStack?: string | null }) { console.error('[page crash]', error, info.componentStack) }
  override componentDidUpdate(prev: { resetKey: string }) { if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }) }
  override render() {
    if (!this.state.error) return this.props.children
    // A lazy chunk that failed to load (deploy happened while the tab was open) needs a reload, not a retry.
    const chunk = /Loading chunk|dynamically imported module|Importing a module script failed/i.test(this.state.error.message)
    return (
      <div role="alert" className="mx-auto flex w-full max-w-lg flex-col items-center gap-3 px-6 py-16 text-center">
        <div className="grid size-12 place-items-center rounded-2xl border border-border bg-surface text-danger-strong shadow-card"><Bug className="size-5" /></div>
        <h2 className="text-md font-semibold text-text">{chunk ? 'A new version of the admin is available' : 'This page hit a problem'}</h2>
        <p className="text-base text-text-muted">{chunk ? 'Reload to get the latest version. Your other tabs are unaffected.' : 'The rest of the admin still works. Try again, or reload if it keeps happening.'}</p>
        {!chunk && <pre className="mt-1 max-w-full overflow-x-auto rounded-control bg-surface-sunken px-3 py-2 text-left font-mono text-xs text-text-muted">{this.state.error.message}</pre>}
        <div className="mt-2 flex gap-2">
          {!chunk && (
            <button type="button" onClick={() => this.setState({ error: null })}
              className="inline-flex h-8 items-center gap-2 rounded-control border border-border-strong bg-surface px-3 text-base font-medium text-text shadow-control hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-focus">
              <RotateCw className="size-4" /> Try again
            </button>
          )}
          <button type="button" onClick={() => window.location.reload()}
            className="inline-flex h-8 items-center rounded-control bg-primary px-3 text-base font-medium text-primary-foreground hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus">
            Reload page
          </button>
        </div>
      </div>
    )
  }
}
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const loc = useLocation()
  return <Boundary resetKey={loc.pathname}>{children}</Boundary>
}
